import React, {
  useState,
  useCallback,
  useEffect,
  ChangeEvent,
  KeyboardEvent,
  useRef,
} from 'react';
import {AurTextField} from '../aur-text-field/AurTextField';
import {IconButton, InputAdornment, TextFieldProps} from '@mui/material';
import {t} from 'i18next';
import AurIcon, {IconNames} from '../aur-icon/AurIcon';
import AurTooltip from '../aur-tooltip/AurTooltip';
import AurLoadingIndicator from '../aur-loading-indicator/AurLoadingIndictor';
import {ReactComponent as SearchIcon} from '../../../assests/svg/search-icon-right.svg';
import {ReactComponent as ClearIcon} from '../../../assests/svg/clear-icon.svg';

// Props for the SearchBox component
interface SearchBoxProps {
  onSearch?: (keyword: string) => void;
  minCharForSearch?: number;
  label?: string;
  info?: boolean;
  classNameProps?: string;
  value?: string;
  searchLoading?: boolean;
  arrowPosition?: 'left' | 'right' | 'top' | 'bottom';
  hintText?: string;
  debounceTimeout?: number;
  onClear?: () => void;
  placeHolder?: string;
  variant?: TextFieldProps['variant'];
  parentClassName?: string;
  dataTestId?: string;
  inputDataTestId?: string;
  className?: string;
  autoComplete?: TextFieldProps['autoComplete'];
  shouldSearchOnMount?: boolean;
}

// Debounce function
const debounce = <T extends (...args: string[]) => void>(
  func: T,
  delay: number
) => {
  let timer: NodeJS.Timeout;
  const debouncedFn = (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };

  debouncedFn.cancel = () => {
    clearTimeout(timer);
  };

  return debouncedFn;
};

const AurSearchBox: React.FC<SearchBoxProps> = ({
  onSearch,
  minCharForSearch = 1,
  label = t('userHomePage.search'),
  info = false,
  classNameProps = 'custom-text-field search-box',
  value = '',
  searchLoading = false,
  arrowPosition = 'top',
  hintText,
  debounceTimeout = 1500,
  placeHolder = '',
  variant = 'filled',
  parentClassName,
  dataTestId,
  inputDataTestId,
  className = '',
  autoComplete,
  shouldSearchOnMount = true,
}) => {
  const [query, setQuery] = useState<string>(value);

  useEffect(() => {
    shouldSearchOnMount && handleSearch(query);
  }, []);

  useEffect(() => {
    setQuery(value);
  }, [value]);
  // Define the search function
  const handleSearch = async (searchQuery: string) => {
    if (onSearch) {
      if (searchQuery.length >= minCharForSearch) {
        onSearch(searchQuery);
      } else {
        onSearch('');
      }
    }
  };

  const handleSearchOnEnterPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === 'Enter' &&
      onSearch &&
      query.length > 0 &&
      query.length < minCharForSearch
    ) {
      debouncedSearch.cancel?.();
      onSearch(query);
      requestAnimationFrame(() => {
        searchRef?.current?.focus();
      });
    }
  };

  // Create a debounced version of the search function
  const debouncedSearch = useCallback(
    debounce(handleSearch, debounceTimeout),
    []
  );

  const searchRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Handle input changes
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newQuery = event.target.value;
    setQuery(newQuery);
    debouncedSearch(newQuery);
    requestAnimationFrame(() => {
      searchRef?.current?.focus();
    });
  };

  // Handle input clear
  const handleClear = () => {
    debouncedSearch.cancel?.();
    setQuery('');
    debouncedSearch('');
    requestAnimationFrame(() => {
      searchRef?.current?.focus();
    });
  };

  const searchIconState = query ? (
    <IconButton
      aria-label={t('buttons.clear')}
      size="small"
      onClick={() => handleClear()}
      data-testid="search-clear-button"
    >
      <ClearIcon {...(info ? {viewBox: '-5.8 0 24 24'} : {})} />
    </IconButton>
  ) : (
    <IconButton
      aria-label={t('common.search')}
      tabIndex={-1}
      size="small"
      disableFocusRipple
      disableRipple
      disabled
    >
      <SearchIcon data-testid="search-icon" />
    </IconButton>
  );

  return (
    <div className={`${parentClassName ?? ''}`}>
      <AurTextField
        autoComplete={autoComplete}
        onChange={handleChange}
        onKeyUp={handleSearchOnEnterPress}
        variant={variant}
        className={`aur-textfield ${classNameProps} ${className}`}
        label={label}
        placeholder={placeHolder}
        value={query}
        inputProps={{
          'aria-label': hintText,
          'data-testid': inputDataTestId,
          ref: {searchRef},
        }}
        data-testid={dataTestId}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {info ? (
                <>
                  {/* <AurIcon icon={IconNames.Search} /> */}
                  {searchIconState}
                  <AurTooltip
                    title={t('userHomePage.searchTooltip')}
                    className="typo-text body4_size tooltop-content"
                    arrowPosition={arrowPosition}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -3],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <span
                      role="img"
                      aria-label={t('userHomePage.searchTooltip')}
                      className="info-padding"
                    >
                      <AurIcon icon={IconNames.Info} />
                    </span>
                  </AurTooltip>
                </>
              ) : searchLoading ? (
                <AurLoadingIndicator show size={24} />
              ) : (
                searchIconState
              )}
            </InputAdornment>
          ),
        }}
      />
    </div>
  );
};

export default AurSearchBox;
